import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import {
  Card,
  Space,
  Typography,
  Button,
  message,
  DatePicker,
  Image,
  Row,
  Col,
  Input,
  InputNumber,
  Select
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { queries, mutations } from '../../../graphql/graphql';
import { Loader } from '../../../components/UI/Loader';
import { ProposalForm } from '../../../components/Proposals/ProposalForm';
import { ProposalStatusTag } from '../../../components/Proposals/ProposalStatusTag';
import Summary from '../../../modules/SiteVisit/Summary';
import type {
  Proposal,
  ProposalFormPayload,
  ProposalStatus,
  ProposalTimeline,
  ProposalTimelineUnit
} from '../../../types/proposal';

const { Title, Text, Paragraph } = Typography;

export const ContractorProposalEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<ProposalFormPayload | null>(null);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
  const [timeline, setTimeline] = useState<ProposalTimeline>({
    from: null,
    to: null,
    unit: 'WEEKS'
  });

  const { data, loading } = useQuery(queries.proposal.PROPOSAL, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
  });
  const [updateProposal, { loading: saving }] = useMutation(mutations.proposal.PROPOSAL_UPDATE);

  const proposal: Proposal | undefined = data?.proposal;
  const { data: siteVisitData, loading: siteVisitLoading } = useQuery(
    queries.siteVisit.GET_SITE_VISIT,
    {
      variables: { id: proposal?.siteVisitId },
      skip: !proposal?.siteVisitId,
    }
  );

  const normalizeValidUntil = (value: unknown): Dayjs | null => {
    if (!value) return null;
    if (typeof value === 'number') return dayjs(value);
    if (typeof value === 'string') {
      if (/^\d+$/.test(value)) {
        const numeric = Number(value);
        const asMillis = value.length <= 10 ? numeric * 1000 : numeric;
        return dayjs(asMillis);
      }
      return dayjs(value);
    }
    return null;
  };

  useEffect(() => {
    if (proposal?.formPayload) {
      setPayload(proposal.formPayload);
    }
    if (proposal?.validUntil) {
      const parsed = normalizeValidUntil(proposal.validUntil);
      setValidUntil(parsed?.isValid() ? parsed : dayjs().add(1, 'month'));
    } else if (proposal) {
      setValidUntil(dayjs().add(1, 'month'));
    }
    if (proposal) {
      setTimeline({
        from:
          typeof proposal.timeline?.from === 'number'
            ? proposal.timeline.from
            : null,
        to: typeof proposal.timeline?.to === 'number' ? proposal.timeline.to : null,
        unit: (proposal.timeline?.unit as ProposalTimelineUnit) || 'WEEKS'
      });
    }
  }, [
    proposal?.formPayload,
    proposal?.validUntil,
    proposal?.timeline?.from,
    proposal?.timeline?.to,
    proposal?.timeline?.unit,
    proposal?.id,
  ]);

  if (loading) {
    return <Loader />;
  }

  if (!proposal) {
    return <Paragraph>Proposal not found.</Paragraph>;
  }

  const address = proposal.mapResult?.map?.address || 'Address pending';
  const snapshot = proposal.mapResult?.snapshot || null;
  const plan = proposal.plan ?? null;
  const planSvgUrl =
    plan?.planSVG && plan?.planId ? `https://3d.homewip.com/svg/${plan.planId}/` : null;
  const planPhotos = Array.isArray(plan?.photos) ? plan.photos : [];
  const isStandalone = proposal.type === 'STANDALONE';

  const handleSave = async (status: ProposalStatus) => {
    if (!payload) {
      message.error('Form data is missing.');
      return;
    }

    const timelineInput = {
      unit: timeline.unit || 'WEEKS',
      ...(typeof timeline.from === 'number' ? { from: timeline.from } : {}),
      ...(typeof timeline.to === 'number' ? { to: timeline.to } : {}),
    };

    try {
      await updateProposal({
        variables: {
          id: proposal.id,
          input: {
            formPayload: payload,
            status,
            validUntil: validUntil ? validUntil.toISOString() : null,
            timeline: timelineInput,
          },
        },
      });
      message.success(status === 'RESPONSES_RECEIVED' ? 'Proposal sent.' : 'Proposal saved.');
      navigate(`/proposal/${proposal.id}`);
    } catch (error: any) {
      message.error(error?.message || 'Failed to update proposal');
    }
  };

  return (
    <div style={{ width: '100%', textAlign: 'left', padding: '24px' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Row gutter={[24, 16]} align="stretch">
            <Col xs={24} md={14} style={{ display: 'flex' }}>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Title level={4} style={{ margin: 0 }}>
                    <ProposalStatusTag status={proposal.status} /> Edit Proposal{' '}
                    {proposal.id.slice(-6).toUpperCase()}
                  </Title>
                  <Text type="secondary">{address}</Text>
                  <Space direction="vertical" size={8}>
                    <Text strong>Valid until</Text>
                    <DatePicker
                      value={validUntil}
                      onChange={value => {
                        setValidUntil(value ?? null);
                      }}
                      format="YYYY-MM-DD"
                      style={{ width: 240 }}
                    />
                  </Space>
                  <Space direction="vertical" size={8}>
                    <Text strong>Estimated timeline</Text>
                    <Space size={8} wrap>
                      <InputNumber
                        min={0}
                        placeholder="From"
                        value={timeline.from ?? undefined}
                        onChange={value =>
                          setTimeline(current => ({
                            ...current,
                            from: typeof value === 'number' ? value : null
                          }))
                        }
                        style={{ width: 120 }}
                      />
                      <Text type="secondary">to</Text>
                      <InputNumber
                        min={0}
                        placeholder="To"
                        value={timeline.to ?? undefined}
                        onChange={value =>
                          setTimeline(current => ({
                            ...current,
                            to: typeof value === 'number' ? value : null
                          }))
                        }
                        style={{ width: 120 }}
                      />
                      <Select
                        value={timeline.unit || 'WEEKS'}
                        onChange={value =>
                          setTimeline(current => ({
                            ...current,
                            unit: value as ProposalTimelineUnit
                          }))
                        }
                        style={{ width: 130 }}
                        options={[
                          { value: 'WEEKS', label: 'weeks' },
                          { value: 'MONTHS', label: 'months' },
                        ]}
                      />
                    </Space>
                  </Space>
                </Space>
                <Space size={12} wrap style={{ marginTop: 'auto' }}>
                  {isStandalone ? (
                    <Button type="primary" onClick={() => handleSave('DRAFT')} loading={saving}>
                      Save
                    </Button>
                  ) : (
                    <>
                      <Button onClick={() => handleSave('DRAFT')} loading={saving}>
                        Save Draft
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => handleSave('RESPONSES_RECEIVED')}
                        loading={saving}>
                        Save and Send
                      </Button>
                    </>
                  )}
                  <Button onClick={() => window.history.back()}>Cancel</Button>
                </Space>
              </div>
            </Col>
            <Col xs={24} md={10}>
              {snapshot && (
                <Image
                  src={snapshot}
                  alt="Map preview"
                  width="100%"
                  style={{ maxHeight: 320, borderRadius: 8, objectFit: 'cover' }}
                  preview
                />
              )}
            </Col>
          </Row>
        </Card>

        {plan && (
          <Card>
            <Title level={5} style={{ marginTop: 0 }}>
              Floor plan
            </Title>
            {planPhotos.length === 0 && !planSvgUrl ? (
              <Paragraph>No plan images available.</Paragraph>
            ) : (
              <Image.PreviewGroup>
                <Row gutter={[16, 16]}>
                  {planSvgUrl && (
                    <Col xs={24}>
                      <Image
                        src={planSvgUrl}
                        alt="Plan SVG"
                        width="100%"
                        style={{ maxHeight: 400, borderRadius: 8, objectFit: 'contain' }}
                        preview
                      />
                    </Col>
                  )}
                  {planPhotos.map((photo, index) => (
                    <Col key={`${photo.original}-${index}`} xs={24} sm={12} md={8}>
                      <Image
                        src={photo.original}
                        alt={`${plan.title || 'Plan'}-${index + 1}`}
                        width="100%"
                        style={{ maxHeight: 240, borderRadius: 8, objectFit: 'cover' }}
                        preview
                      />
                    </Col>
                  ))}
                </Row>
              </Image.PreviewGroup>
            )}
          </Card>
        )}

        {siteVisitLoading && <Paragraph>Loading site plan summary...</Paragraph>}
        {siteVisitData?.siteVisit && (
          <Card>
            <Title level={5} style={{ marginTop: 0 }}>
              Site Plan
            </Title>
            <Summary siteVisit={siteVisitData.siteVisit} showShare={false} />
          </Card>
        )}

        {payload ? (
          <>
            <ProposalForm payload={payload} onChange={setPayload} />
            <Card>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>Exclusions</Text>
                <Input.TextArea
                  value={payload.contractorNotes ?? ''}
                  onChange={event => {
                    const nextValue = event.target.value;
                    setPayload(current =>
                      current
                        ? {
                            ...current,
                            contractorNotes: nextValue.trim() ? nextValue : null
                          }
                        : current
                    );
                  }}
                  placeholder="Add exclusions or other notes for the client"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                />
              </Space>
            </Card>
          </>
        ) : (
          <Paragraph>No proposal form data yet.</Paragraph>
        )}

        <Card>
          <Space size={12}>
            {isStandalone ? (
              <Button type="primary" onClick={() => handleSave('DRAFT')} loading={saving}>
                Save
              </Button>
            ) : (
              <>
                <Button onClick={() => handleSave('DRAFT')} loading={saving}>
                  Save Draft
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleSave('RESPONSES_RECEIVED')}
                  loading={saving}>
                  Save and Send
                </Button>
              </>
            )}
            <Button onClick={() => window.history.back()}>Cancel</Button>
          </Space>
        </Card>
      </Space>
    </div>
  );
};
